import { useEffect, useRef, useState } from "react";

/**
 * # useSmoothText — typewriter reveal at the render layer
 *
 * Decouples the *display* rate of streamed assistant text from the *network*
 * arrival rate. The Scout store keeps accumulating the full text exactly as it
 * does today; this hook just reveals it gradually so paragraphs flow in
 * character-by-character instead of popping in network-sized bursts.
 *
 * Given the full `target` text and an `enabled` flag, it returns the substring
 * that should currently be shown:
 *
 * - While `enabled`, a `requestAnimationFrame` loop advances a `displayedLength`
 *   cursor toward `target.length`. Each frame's advance is delta-time based: the
 *   larger of a steady base rate ({@link CHARS_PER_SECOND}) and an adaptive
 *   catch-up term that drains a fraction of the backlog per 60Hz-reference frame,
 *   so small trickles reveal smoothly while large bursts speed up to catch up
 *   without ever lagging far behind. Sub-character progress is carried across
 *   frames, so the reveal runs at the same wall-clock speed on a 60Hz or a 120Hz
 *   display instead of revealing twice as fast on high-refresh-rate screens.
 * - When `enabled` is `false` (stream ended/aborted) or the target shrinks
 *   (a reused slot now holds a different/shorter message), it snaps to the full
 *   target immediately — the final text is never withheld and history never
 *   re-animates on remount.
 *
 * Feeding the truncated prefix through the existing `marked.lexer()` pipeline is
 * safe: the renderer is already fed partial mid-token text constantly during
 * real streaming, so truncating to an arbitrary character boundary is consistent
 * with current behavior.
 */

// Taste constants — steady reveal cadence and how aggressively to catch up on a
// large backlog. Advances are delta-time based and calibrated against a 60Hz
// reference frame, so the reveal feels identical regardless of the display's
// actual refresh rate. Tune live until the reveal matches the desired feel.
const CHARS_PER_SECOND = 60;
const CATCHUP_DIVISOR = 8;
const REFERENCE_HZ = 60;

export function useSmoothText(target: string, { enabled }: { enabled: boolean }): string {
  const [displayedLength, setDisplayedLength] = useState(() => (enabled ? 0 : target.length));

  // Mirror the latest displayed length into a ref so the rAF loop can read it
  // without being re-created on every reveal step.
  const displayedLengthRef = useRef(displayedLength);
  displayedLengthRef.current = displayedLength;

  const frameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);

  // Carries sub-character progress between frames so the reveal rate stays
  // refresh-rate independent — a 120Hz display paints twice as often, but each
  // frame's delta is half as large, netting the same chars/second.
  const revealBudgetRef = useRef(0);

  const cancelFrame = (): void => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    lastFrameTimeRef.current = null;
    revealBudgetRef.current = 0;
  };

  // Snap to full whenever reveal is disabled or the target shrinks/changes to
  // something shorter than what we've already shown. Growth is handled by the
  // animation effect below.
  useEffect(() => {
    const shouldSnap = !enabled || target.length < displayedLengthRef.current;
    if (!shouldSnap) return;

    cancelFrame();
    if (displayedLengthRef.current !== target.length) {
      setDisplayedLength(target.length);
    }
  }, [enabled, target]);

  // Drive the reveal toward the (possibly growing) target while enabled. This
  // effect re-runs when the target grows, restarting the loop from wherever the
  // cursor currently sits.
  useEffect(() => {
    if (!enabled) return;
    if (displayedLengthRef.current >= target.length) return;

    const step = (now: number): void => {
      const previousFrameTime = lastFrameTimeRef.current ?? now;
      const elapsedSeconds = Math.max(0, (now - previousFrameTime) / 1000);
      lastFrameTimeRef.current = now;

      const current = displayedLengthRef.current;
      const remaining = target.length - current;
      if (remaining <= 0) {
        cancelFrame();
        return;
      }

      // Steady trickle, in (fractional) chars, for this frame's elapsed time.
      const baseAdvance = CHARS_PER_SECOND * elapsedSeconds;
      // Catch-up drains a fixed fraction of the backlog per 60Hz reference frame
      // (remaining / CATCHUP_DIVISOR at 60Hz), expressed as a rate so large
      // bursts catch up at the same wall-clock pace on any refresh rate.
      const referenceFrames = elapsedSeconds * REFERENCE_HZ;
      const catchUpAdvance = remaining * (1 - Math.pow(1 - 1 / CATCHUP_DIVISOR, referenceFrames));

      // Carry sub-character progress across frames so a slow trickle reveals at
      // exactly CHARS_PER_SECOND rather than rounding up to one char per frame.
      revealBudgetRef.current += Math.max(baseAdvance, catchUpAdvance);
      const advance = Math.min(remaining, Math.floor(revealBudgetRef.current));
      if (advance <= 0) {
        frameRef.current = requestAnimationFrame(step);
        return;
      }
      revealBudgetRef.current -= advance;

      const next = current + advance;
      setDisplayedLength(next);

      if (next < target.length) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        cancelFrame();
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return cancelFrame;
  }, [enabled, target]);

  // Cancel any in-flight frame on unmount.
  useEffect(() => cancelFrame, []);

  if (!enabled) return target;
  return target.slice(0, Math.min(displayedLength, target.length));
}
